/* eslint-disable max-len */
// src/components/Onboarding.js
// Help and onboarding page for NextAInsight users.
// Contains: Getting Started guide, App Dictionary, Safe Posting Guidelines, Contact info.

import React from 'react';
import { FiBook, FiHelpCircle, FiShield, FiMail, FiCheckCircle, FiArrowRight } from 'react-icons/fi';
import { SUPPORT_EMAIL } from '../shared/constants/contactInfo';

function Onboarding() {
    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8 bg-warm-dark min-h-screen text-cream">
            
            {/* Header */}
            <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center p-3 bg-slate-blue/20 rounded-2xl mb-4 shadow-glow">
                    <FiHelpCircle className="h-8 w-8 text-slate-blue" />
                </div>
                <h1 className="text-4xl font-black text-white tracking-tight leading-tight">Help Center</h1>
                <p className="text-cream/50 mt-2 text-lg">Everything you need to know about NextAInsight</p>
            </div>

            {/* Getting Started */}
            <section className="bg-warm-gray/10 backdrop-blur-md rounded-[2rem] shadow-xl border border-white/5 p-8 relative overflow-hidden group hover:border-slate-blue/30 transition-all duration-500">
                <div className="absolute top-0 right-0 w-64 h-64 bg-slate-blue/5 rounded-full blur-[80px] pointer-events-none group-hover:bg-slate-blue/10 transition-colors"></div>
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-slate-blue/20 rounded-xl">
                        <FiBook className="h-6 w-6 text-slate-blue" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Getting Started</h2>
                </div>
                
                <div className="space-y-6 relative z-10">
                    <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/5">
                        <FiCheckCircle className="h-6 w-6 text-pastel-green mt-1 flex-shrink-0" />
                        <div>
                            <h3 className="font-bold text-white text-lg mb-1">1. Create a Project</h3>
                            <p className="text-sm text-cream/60 leading-relaxed">Define your brand's industry, voice, and visual style. This powers all AI-generated content.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/5">
                        <FiCheckCircle className="h-6 w-6 text-pastel-green mt-1 flex-shrink-0" />
                        <div>
                            <h3 className="font-bold text-white text-lg mb-1">2. Connect Your Social Accounts</h3>
                            <p className="text-sm text-cream/60 leading-relaxed">Link your Facebook Page and Instagram Business account in Settings to enable publishing.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/5">
                        <FiCheckCircle className="h-6 w-6 text-pastel-green mt-1 flex-shrink-0" />
                        <div>
                            <h3 className="font-bold text-white text-lg mb-1">3. Generate Content</h3>
                            <p className="text-sm text-cream/60 leading-relaxed">Use Express Lane for single posts, or set up Recurring Streams for automated daily content.</p>
                        </div>
                    </div>
                    <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/5">
                        <FiArrowRight className="h-6 w-6 text-slate-blue mt-1 flex-shrink-0" />
                        <div>
                            <h3 className="font-bold text-white text-lg mb-1">4. Review & Publish</h3>
                            <p className="text-sm text-cream/60 leading-relaxed">All content goes to your Content Queue for review. AI visuals are generated automatically or on-demand.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* App Dictionary */}
            <section className="bg-warm-gray/10 backdrop-blur-md rounded-[2rem] shadow-xl border border-white/5 p-8 relative overflow-hidden group hover:border-slate-blue/30 transition-all duration-500">
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-slate-blue/20 rounded-xl">
                        <FiHelpCircle className="h-6 w-6 text-slate-blue" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">App Dictionary</h2>
                </div>
                
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                    {[
                        { term: 'Project', def: 'A container for a brand/campaign with its own identity, voice, and visual settings.' },
                        { term: 'Content Pillar', def: 'A core topic or theme that guides AI-generated content (e.g., "Industry Tips", "Customer Stories").' },
                        { term: 'Express Lane', def: 'Single post workflow: create one piece of content with custom input or AI assistance.' },
                        { term: 'Recurring Stream', def: 'Auto-generates posts on a schedule (e.g., 1 post/day for 30 days) based on your content pillars.' },
                        { term: 'Content Queue', def: 'Ready-to-publish posts awaiting their scheduled time. All posts go here for final review.' },
                        { term: 'Auto Painter', def: 'Automated visual fulfillment \u2014 processes queued visuals on a regular schedule.' },

                        { term: 'Visual Studio', def: 'AI-powered image generator with style presets, text strategies, and format options.' },
                    ].map((item, i) => (
                        <div key={i} className="p-5 bg-black/20 rounded-2xl border border-white/5 hover:border-white/10 transition-colors">
                            <dt className="font-bold text-slate-blue mb-2">{item.term}</dt>
                            <dd className="text-sm text-cream/60 leading-relaxed">{item.def}</dd>
                        </div>
                    ))}
                </dl>
            </section>

            {/* Safe Posting Guidelines */}
            <section className="bg-warm-gray/10 backdrop-blur-md rounded-[2rem] shadow-xl border border-white/5 p-8 relative overflow-hidden mt-8">
                 <div className="absolute top-0 left-0 w-64 h-64 bg-pastel-green-bg/10 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex items-center gap-4 mb-8">
                    <div className="p-3 bg-pastel-green-bg/20 rounded-xl">
                        <FiShield className="h-6 w-6 text-pastel-green" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Safe Posting Guidelines</h2>
                </div>
                
                <div className="space-y-5 text-sm text-cream/80 relative z-10">
                    {[
                        "Always review AI content before publishing. AI-generated text and images may contain errors, inaccuracies, or unintended content.",
                        "Verify factual claims. Don't publish statistics, quotes, or claims without verifying their accuracy.",
                        "Check image content carefully. AI images may contain visual artifacts, inappropriate elements, or unintended text.",
                        "Ensure brand alignment. Confirm all content matches your brand voice, values, and messaging guidelines.",
                        "Respect platform policies. Ensure all content complies with Facebook and Instagram community guidelines."
                    ].map((text, i) => (
                        <div key={i} className="flex items-start gap-4">
                            <span className="font-bold text-slate-blue bg-slate-blue/10 w-6 h-6 rounded-full flex items-center justify-center text-xs flex-shrink-0 border border-slate-blue/20">{i + 1}</span>
                            <p className="leading-relaxed"><strong className="text-white block mb-1">{text.split('.')[0]}.</strong>{text.split('.').slice(1).join('.')}</p>
                        </div>
                    ))}
                </div>
                
                <div className="mt-8 p-6 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl relative z-10">
                    <p className="text-sm text-yellow-200/80 leading-relaxed flex gap-3">
                        <span className="text-xl">⚠️</span>
                        <span>
                            <strong className="text-white">Important:</strong> You are fully responsible for all content published through NextAInsight. 
                            AI-generated content requires human review and approval before publishing.
                        </span>
                    </p>
                </div>
            </section>

            {/* Contact */}
            <section className="bg-warm-gray/10 backdrop-blur-md rounded-[2rem] shadow-xl border border-white/5 p-8 text-center">
                <div className="inline-flex items-center justify-center p-3 bg-slate-blue/20 rounded-xl mb-4">
                    <FiMail className="h-6 w-6 text-slate-blue" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Still have questions?</h2>
                <p className="text-cream/50 mb-6"> Our support team is here to help you get the most out of NextAInsight.</p>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center px-8 py-3 bg-white/5 hover:bg-white/10 text-white text-sm font-bold uppercase tracking-widest rounded-full backdrop-blur-md border border-white/10 transition-all hover:scale-105 hover:border-slate-blue/50 group">
                    <span className="group-hover:text-slate-blue transition-colors">{SUPPORT_EMAIL}</span>
                </a>
            </section>

        </div>
    );
}

export default Onboarding;
